import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Store, Utensils, Building2, Home, Cross, MapPin } from "lucide-react";
import PayPalCheckout from "../components/PayPalCheckout";
import PayPalProvider from "../components/PayPalProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  "storefront-no-parking": Store,
  "storefront-with-parking": Store,
  "restaurant-no-parking": Utensils,
  "restaurant-with-parking": Utensils,
  "office-with-parking": Building2,
  "residential-basic": Home,
  "residential-driveway": Home,
  "church-no-parking": Cross,
  "church-with-parking": Cross,
  "lot-maintenance": MapPin,
};

export default function Pricing() {
  const [matrix, setMatrix] = useState(null);
  const [categoryId, setCategoryId] = useState("storefront-with-parking");
  const [size, setSize] = useState("small");
  const [cadence, setCadence] = useState("monthly");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loadingTier, setLoadingTier] = useState(null);
  const [taxZip, setTaxZip] = useState("");
  const [taxPreview, setTaxPreview] = useState(null);

  useEffect(() => { axios.get(`${API}/pricing`).then((r) => setMatrix(r.data)); }, []);

  if (!matrix) return null;

  const category = matrix.categories.find((c) => c.id === categoryId);
  const sizeMeta = category?.sizes?.[size];
  const prices = category?.pricing?.[size] || {};

  // Cadence discount system
  const CADENCE = {
    monthly:   { label: "Monthly",   mult: 1,    save: 0,  period: 1,  suffix: "/mo" },
    quarterly: { label: "Quarterly", mult: 0.95, save: 5,  period: 3,  suffix: "/mo" },
    annual:    { label: "Annual",    mult: 0.88, save: 12, period: 12, suffix: "/mo" },
  };

  async function subscribe(tier) {
    if (!name || !email) { toast.error("Provide your name & email first."); return; }
    setLoadingTier(tier);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: `plan:${categoryId}:${size}:${tier}:${cadence}`,
        origin_url: window.location.origin,
        customer_email: email, customer_name: name,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch { toast.error("Checkout failed — try again."); setLoadingTier(null); }
  }

  function requestEnterprise() {
    if (!name || !email) { toast.error("Provide your name & email first."); return; }
    axios.post(`${API}/quote`, {
      name, email, service: "Enterprise · Multi-location",
      message: `Enterprise inquiry from Pricing · ${category.label} · ${sizeMeta?.label || size}`,
    }).then(() => toast.success("Received. An operator will call within 24 hours."))
      .catch(() => toast.error("Submission failed — please try again."));
  }

  const founding = matrix.founding;
  const seatsLeft = Math.max(0, (founding?.seats_total || 0) - (founding?.seats_claimed || 0));

  return (
    <PayPalProvider mode="subscription">
    <div data-testid="pricing-page" style={{ background: "var(--ivory)" }}>
      {/* Founding-100 hook */}
      {founding?.active && (
        <section className="pt-14 pb-8">
          <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="glass-card p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" style={{ borderColor: "var(--line-gold)" }}>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--gold)" }}>◇ Founding 100 · Locked-in for life</div>
                <div className="font-display text-2xl md:text-3xl mt-2" style={{ letterSpacing: "-0.02em", color: "var(--text)" }}>{founding.sub}</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-display text-5xl md:text-6xl" style={{ color: "var(--gold-2)", letterSpacing: "-0.03em" }}>{seatsLeft}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em] mt-1" style={{ color: "var(--text-3)" }}>Seats remaining</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="pt-4 pb-8">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>Chapter Four · Instant Plans</div>
          <h1 className="font-display mt-4" style={{ fontSize: "clamp(3rem, 8vw, 7rem)", lineHeight: 0.9, letterSpacing: "-0.04em" }}>
            Pick your <span className="font-display-italic" style={{ color: "var(--brass)" }}>plan.</span>
          </h1>
          <p className="mt-6 font-body text-xl max-w-3xl" style={{ color: "var(--navy-soft)" }}>
            No quotes. No callbacks. Choose your property type, choose your size, pick a tier — locked in and running before you close this tab.
          </p>
        </div>
      </section>

      {/* Step 1: Category */}
      <section className="pb-6">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: "var(--brass)" }}>Step 01 · Property Type</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {matrix.categories.map((c) => {
              const Icon = CATEGORY_ICONS[c.id] || Store;
              const active = categoryId === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`cat-${c.id}`}
                  onClick={() => setCategoryId(c.id)}
                  data-cursor="hover"
                  data-cursor-label="Select"
                  className="thin-border p-5 text-left transition-all duration-300"
                  style={{ background: active ? "rgba(198,168,124,0.10)" : "rgba(255,255,255,0.02)", color: active ? "var(--gold)" : "var(--text)", borderColor: active ? "var(--gold)" : "var(--line)" }}
                >
                  <Icon size={22} style={{ color: active ? "var(--gold)" : "var(--text-2)" }} strokeWidth={1.2} />
                  <div className="font-display text-lg mt-3 leading-tight" style={{ letterSpacing: "-0.02em" }}>{c.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Step 2: Size */}
      <section className="pb-6">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: "var(--brass)" }}>Step 02 · Size</div>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(category.sizes).map(([sz, meta]) => {
              const active = size === sz;
              return (
                <button
                  key={sz}
                  data-testid={`size-${sz}`}
                  onClick={() => setSize(sz)}
                  data-cursor="hover"
                  data-cursor-label="Select"
                  className="thin-border p-5 text-left transition-all duration-300"
                  style={{ background: active ? "rgba(198,168,124,0.10)" : "rgba(255,255,255,0.02)", color: active ? "var(--gold)" : "var(--text)", borderColor: active ? "var(--gold)" : "var(--line)" }}
                >
                  <div className="font-display text-2xl" style={{ letterSpacing: "-0.02em" }}>{meta.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.24em] mt-1" style={{ color: active ? "var(--gold-2)" : "var(--text-3)" }}>{meta.sqft}</div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Customer capture */}
      <section className="pb-8">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="glass-card p-6 grid sm:grid-cols-2 gap-4">
            <input data-testid="pricing-name" className="field-boxed" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="pricing-email" className="field-boxed" placeholder="Business email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
      </section>

      {/* Live tax preview */}
      <section className="pb-8" data-testid="tax-preview-section">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--gold)" }}>See Tax by ZIP</div>
                <div className="font-body text-sm mt-1" style={{ color: "var(--text-2)" }}>Enter your property ZIP to preview NY/NC sales tax before checkout.</div>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--text-3)" }}>
                Rates: NYC 8.875% · LI 8.625% · Westchester 8.375% · Charlotte 8.25%
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                data-testid="tax-preview-zip"
                className="field-boxed w-40 tracking-widest"
                placeholder="10001"
                maxLength={5}
                value={taxZip}
                onChange={async (e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0,5);
                  setTaxZip(v);
                  if (v.length === 5 && prices?.standard) {
                    try {
                      const r = await axios.get(`${API}/tax-preview?zip=${v}&amount=${prices.standard * CADENCE[cadence].mult}`);
                      setTaxPreview(r.data);
                    } catch { setTaxPreview(null); }
                  } else setTaxPreview(null);
                }}
              />
              {taxPreview && (
                <div className="flex items-baseline gap-4 font-mono text-[11px]" style={{ color: "var(--text-2)" }}>
                  <span data-testid="tax-preview-jurisdiction">{taxPreview.jurisdiction}</span>
                  <span style={{ color: "var(--gold-2)" }}>·</span>
                  <span>Base: <strong style={{ color: "white" }}>${taxPreview.amount.toFixed(2)}</strong></span>
                  <span>Tax ({(taxPreview.rate * 100).toFixed(3)}%): <strong style={{ color: "white" }}>${taxPreview.tax.toFixed(2)}</strong></span>
                  <span style={{ color: "var(--gold-2)" }}>·</span>
                  <span data-testid="tax-preview-total">Total: <strong style={{ color: "var(--gold)" }}>${taxPreview.total.toFixed(2)}{CADENCE[cadence].suffix}</strong></span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Billing cadence toggle */}
      <section className="pb-8">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: "var(--gold)" }}>Step 03 · Billing Cadence</div>
          <div
            className="inline-flex p-1.5 gap-1"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid var(--line)",
              borderRadius: "var(--r-full)",
              backdropFilter: "blur(12px)",
            }}
            data-testid="cadence-toggle"
          >
            {Object.entries(CADENCE).map(([key, meta]) => {
              const active = cadence === key;
              return (
                <button
                  key={key}
                  data-testid={`cadence-${key}`}
                  onClick={() => setCadence(key)}
                  data-cursor="hover"
                  className="relative flex items-center gap-2 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.32em] transition-all duration-500"
                  style={{
                    background: active ? "var(--gold)" : "transparent",
                    color: active ? "#0b0b0d" : "var(--text-2)",
                    borderRadius: "var(--r-full)",
                    boxShadow: active ? "0 4px 20px rgba(198,168,124,0.4)" : "none",
                  }}
                >
                  {meta.label}
                  {meta.save > 0 && (
                    <span
                      className="ml-1 px-2 py-0.5 font-mono text-[9px]"
                      style={{
                        background: active ? "rgba(11,11,13,0.15)" : "rgba(198,168,124,0.15)",
                        color: active ? "#0b0b0d" : "var(--gold)",
                        borderRadius: "var(--r-full)",
                        letterSpacing: "0.16em",
                      }}
                    >
                      −{meta.save}%
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Step 4: Tiers */}
      <section className="pb-24">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--gold)" }}>Step 04 · Pick Your Tier</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] mt-1" style={{ color: "var(--text-3)" }}>{category.label} · {sizeMeta.label} · {sizeMeta.sqft} · Billed {CADENCE[cadence].label.toLowerCase()}</div>
            </div>
          </div>
          <p className="mt-2 mb-6 font-body max-w-2xl" style={{ color: "var(--text-2)" }}>{category.blurb}</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {["essentials", "standard", "elite"].map((tier, i) => {
              const meta = matrix.tiers[tier];
              const basePrice = prices[tier];
              const cad = CADENCE[cadence];
              const effectivePrice = Math.round(basePrice * cad.mult);
              const totalBilled = Math.round(effectivePrice * cad.period);
              const featured = tier === "standard";
              return (
                <motion.div
                  key={tier}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className={`glass-card p-8 md:p-10 flex flex-col relative ${featured ? "md:-translate-y-3" : ""}`}
                  data-testid={`tier-${tier}`}
                  style={featured ? { borderColor: "var(--gold)", boxShadow: "0 44px 80px -20px rgba(0,0,0,0.85), 0 0 60px rgba(198,168,124,0.2)" } : undefined}
                >
                  {featured && (
                    <div className="absolute -top-3 left-8 pill">Most Popular</div>
                  )}
                  <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: featured ? "var(--gold)" : "var(--text-3)" }}>Tier · 0{i + 1}</div>
                  <div className="font-display text-4xl md:text-5xl mt-3" style={{ letterSpacing: "-0.03em", color: "var(--text)" }}>{meta.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em] mt-2" style={{ color: "var(--text-3)" }}>{meta.cadence}</div>
                  <div className="mt-8 flex items-baseline gap-2">
                    <span className="font-display text-3xl" style={{ color: "var(--gold-2)" }}>$</span>
                    <span className="font-display text-6xl md:text-7xl leading-none" style={{ letterSpacing: "-0.04em", color: "var(--text)" }}>{effectivePrice}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.32em] ml-2" style={{ color: "var(--text-3)" }}>/mo</span>
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.32em] mt-2" style={{ color: "var(--text-3)" }}>
                    {cad.save > 0 ? `$${totalBilled} billed ${cad.label.toLowerCase()} · Save ${cad.save}%` : `Billed monthly · Cancel anytime`}
                  </div>

                  <ul className="mt-8 space-y-3.5">
                    {meta.features.map((f, idx) => (
                      <li key={idx} className="flex items-start gap-3 font-body text-sm" style={{ color: "var(--text-2)" }}>
                        <Check size={14} className="mt-1 shrink-0" style={{ color: "var(--gold)" }} strokeWidth={1.5} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => subscribe(tier)}
                    disabled={loadingTier === tier}
                    data-testid={`subscribe-${tier}`}
                    data-cursor="hover"
                    data-cursor-label="Book"
                    className={`mt-10 w-full justify-center ${featured ? "btn-glow" : "btn-ghost"}`}
                  >
                    {loadingTier === tier ? "Redirecting…" : "Lock In · Pay Now"} <ArrowUpRight size={14} />
                  </button>

                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1" style={{ height: 1, background: "var(--line)" }} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: "var(--text-3)" }}>or</span>
                    <div className="flex-1" style={{ height: 1, background: "var(--line)" }} />
                  </div>

                  <div className="mt-3">
                    <PayPalCheckout
                      testId={`paypal-plan-${tier}`}
                      subscription={true}
                      orderPayload={{
                        package_id: `plan:${categoryId}:${size}:${tier}:${cadence}`,
                        customer_email: email,
                        customer_name: name,
                      }}
                      validate={() => Boolean(name && email)}
                      validateMsg="Provide your name & email before paying with PayPal."
                    />
                  </div>
                </motion.div>
              );
            })}

            {/* Enterprise / Custom tier */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="glass-card p-8 md:p-10 flex flex-col relative overflow-hidden"
              data-testid="tier-enterprise"
              style={{
                background: "linear-gradient(155deg, rgba(198,168,124,0.10), rgba(20,20,23,0.55) 60%)",
                borderColor: "var(--line-gold)",
              }}
            >
              {/* Ambient corner glow */}
              <div className="absolute -top-20 -right-20 w-64 h-64 pointer-events-none" style={{
                background: "radial-gradient(circle, rgba(198,168,124,0.28), transparent 60%)",
                filter: "blur(20px)",
              }} />

              <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--gold)" }}>Tier · Bespoke</div>
              <div className="font-display text-4xl md:text-5xl mt-3" style={{ letterSpacing: "-0.03em", color: "var(--text)" }}>Enterprise</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] mt-2" style={{ color: "var(--text-3)" }}>Multi-location · Custom scope</div>

              <div className="mt-8 flex items-baseline gap-2">
                <span className="font-display text-6xl md:text-7xl leading-none" style={{ letterSpacing: "-0.04em", color: "var(--gold-2)" }}>Custom</span>
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.32em] mt-2" style={{ color: "var(--text-3)" }}>
                Volume pricing · Dedicated ops manager · SLAs
              </div>

              <ul className="mt-8 space-y-3.5">
                {[
                  "Multi-property portfolio management",
                  "Dedicated ops manager · single contact",
                  "Custom SLAs · uptime-style guarantees",
                  "Centralized reporting dashboard",
                  "Volume-based rate lock",
                  "COI + additional insured — same day",
                ].map((f, idx) => (
                  <li key={idx} className="flex items-start gap-3 font-body text-sm" style={{ color: "var(--text-2)" }}>
                    <Check size={14} className="mt-1 shrink-0" style={{ color: "var(--gold)" }} strokeWidth={1.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={requestEnterprise}
                data-testid="enterprise-cta"
                data-cursor="hover"
                data-cursor-label="Request"
                className="btn-glow mt-10 w-full justify-center"
              >
                Request Proposal <ArrowUpRight size={14} />
              </button>
            </motion.div>
          </div>

          <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--text-3)" }}>
            Auto-renews · Cancel anytime · Payments secured by Stripe or PayPal · Every plan includes storm express re-clean within 48–72 hrs
          </p>
        </div>
      </section>
    </div>
    </PayPalProvider>
  );
}
